import {
  Archive,
  ArrowRight,
  BookOpen,
  CalendarClock,
  FlaskConical,
  Globe,
  History,
  KeyRound,
  LayoutTemplate,
  Lock,
  Network,
  Play,
  Plug,
  Radar,
  Server,
  ShieldCheck,
  SquareTerminal,
  Gauge,
  Wrench,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  AutoFixArt,
  CanaryArt,
  CommandFanoutArt,
  PlaybookArt,
  RestoreTestArt,
  RunHistoryArt,
  TemplateArt,
} from '@/modules/marketing/AutomationArt';
import { RolloutDemo } from '@/modules/marketing/demo/RolloutDemo';
import { BackupArt, MonitorArt } from '@/modules/marketing/FeatureArt';
import { HeroPanel } from '@/modules/marketing/HeroPanel';
import { LANDING_FAQ } from '@/modules/marketing/landing-faq';
import { TRIAL } from '@/modules/marketing/pricing-data';
import { PricingTable } from '@/modules/marketing/PricingTable';
import { SiteFooter } from '@/modules/marketing/SiteFooter';
import { SiteHeader } from '@/modules/marketing/SiteHeader';
import { usePageMeta } from '@/lib/seo';
import { countOf } from '@/lib/trial';
import { PAGES } from '@/seo/pages';

/**
 * The public landing page.
 *
 * ## Every claim here is checkable against the code
 *
 * A standing instruction on this project: **do not invent company addresses, certifications,
 * customer counts or testimonials, and do not make unsupported security claims.** So there are none.
 * No logo wall of customers, no "trusted by 10,000 teams", no SOC 2 badge, no uptime percentage
 * nobody has measured.
 *
 * That constraint shapes the page rather than limiting it. With no social proof available, trust has
 * to come from *specificity*: "a sudo password is written to sudo's standard input, never onto a
 * command line" is checkable, and a badge is not. The section on what the platform deliberately does
 * **not** do sits just above pricing, where saying the unflattering thing at the moment of decision
 * reads as confidence rather than as an apology.
 *
 * ## Three products, one story
 *
 * Automation leads, because it is what a customer does every day. Monitoring with auto-fix and
 * backups with verification follow as the two things that keep a fleet healthy once a change has
 * gone out — premium, first-class, and described in the same visual language so they read as one
 * platform rather than three brochures.
 *
 * ## What the page must never describe again
 *
 * The platform stopped deploying applications and managing domains, DNS, databases and scheduled
 * jobs. None of those appear here, even as a history lesson: a visitor reads a landing page as a list
 * of what they are buying. Certificates and firewalls came back in a different shape — as playbooks a
 * customer runs on their own server (a reverse proxy with Let's Encrypt, and Fail2Ban with a
 * firewall) — and the page describes them only that way, never as something the platform manages.
 *
 * ## A free trial, stated with its limits
 *
 * Every new account starts a **7-day free trial with no card**: one server, three monitors, two backup
 * sources and 25 Command Runner runs, everything else included, and one person. The page says so with the limits
 * attached, because a trial described only as "free" is discovered to have limits by being refused
 * something. There is no free plan after it, and the **7-day money-back guarantee on a first payment**
 * still applies to the first real purchase.
 *
 * ## Provider wordmarks, not brand marks
 *
 * A typeset strip rather than logos: drawing eight marks from memory would produce eight subtly wrong
 * versions of other companies' trademarks on a commercial page. The strip makes the same statement
 * ("bring a server from any of these") and is correct.
 */

/** The vertical rhythm, in one place so sections cannot drift apart. */
const SECTION = 'py-16 sm:py-20';

/**
 * Providers customers are most likely to already have an account with.
 *
 * Not a partner list and not an integration list. The platform works with any Linux machine reachable
 * over SSH, which is all of these and many more; the line beneath the strip says so, because a row of
 * names on a landing page otherwise implies a relationship that does not exist.
 */
const PROVIDERS = ['DigitalOcean', 'Hetzner', 'Vultr', 'AWS', 'Google Cloud', 'Azure', 'OVHcloud', 'Contabo'];

/**
 * What playbooks run on. The minimum versions are on each playbook; this is the family list.
 *
 * The versions named in the sentence beside these chips are the oldest releases their vendors still
 * give standard security support: Ubuntu 22.04, Debian 12, RHEL-compatible 8, Amazon Linux 2023. Some
 * older playbooks still accept Ubuntu 20.04 or Debian 11, and each says so on its own page in the
 * dashboard, but a release past its end of support is not something to advertise.
 */
const DISTRIBUTIONS = [
  'Ubuntu 22.04+',
  'Debian 12+',
  'AlmaLinux 8+',
  'Rocky Linux 8+',
  'RHEL 8+',
  'CentOS Stream 9+',
  'Oracle Linux 8+',
  'Amazon Linux 2023',
];

/** Twenty-four in the catalogue; these are the ones a visitor is most likely to be looking for. */
const PLAYBOOKS = [
  'Update Packages',
  'Restart Services',
  'Clean Disk Space',
  'Fail2Ban & Firewall Hardening',
  'User Management',
  'Install Docker',
  'Install Kubernetes',
  'Install Node.js',
  'Install PHP',
  'Install Python',
  'Install Nginx',
  'Configure Reverse Proxy',
  'Certificate Renewal',
  'Prepare CI Runner',
  'Install MariaDB',
  'Install Redis',
];

const TEMPLATES = [
  'WordPress Server',
  'Laravel Server',
  'Node.js Server',
  'Python Production Stack',
  'Nginx Reverse Proxy + Auto-Renewing SSL',
  'CI/CD Self-Hosted Runner Host',
  'Kubernetes Node Setup',
  'Docker Host',
  'Monitoring Server',
];

/**
 * The platform, as nine capabilities in three products.
 *
 * Headings are outcomes rather than module names: a visitor deciding whether to sign up is asking
 * "what do I stop having to do", not "what is on the sidebar". The chips underneath are the
 * mechanism, for the reader who has decided the outcome is interesting and now wants to know whether
 * it is real. Every card links to the article that goes into detail.
 */
const CAPABILITIES: {
  art: () => JSX.Element;
  icon: LucideIcon;
  group: string;
  title: string;
  body: string;
  chips: string[];
  to: string;
}[] = [
  {
    art: PlaybookArt,
    icon: BookOpen,
    group: 'Playbooks',
    title: 'Playbooks for every distribution',
    body: 'Update packages, install PHP, Python or Docker, harden SSH with Fail2Ban and a firewall. Each playbook detects the distribution, version and package manager, and runs the right commands.',
    chips: ['apt, dnf and yum', 'Dry run', 'Safe to rerun'],
    to: '/features/playbooks',
  },
  {
    art: CommandFanoutArt,
    icon: SquareTerminal,
    group: 'Command runner',
    title: 'Run commands across your fleet',
    body: 'Target servers by name, group, tag or operating system. Follow live output from each server, and retry only the ones that failed.',
    chips: ['Live output', 'Retry failures', 'Non-interactive'],
    to: '/features/command-runner',
  },
  {
    art: TemplateArt,
    icon: LayoutTemplate,
    group: 'Templates',
    title: 'Prepare a server in one run',
    body: 'WordPress, Laravel, Node.js and Python app servers, an Nginx reverse proxy with Let’s Encrypt, CI runner hosts, Docker and monitoring servers, each built from tested playbooks.',
    chips: ['Versioned', 'Sensible defaults', 'Per-step results'],
    to: '/features/server-templates',
  },
  {
    art: CanaryArt,
    icon: ShieldCheck,
    group: 'Safe rollouts',
    title: 'Roll out changes safely',
    body: 'Preflight checks on every server, one canary server confirmed by a person, then batches that stop when failures pass your limit.',
    chips: ['Preflight', 'Canary server', 'Batches'],
    to: '/features/safe-rollouts',
  },
  {
    art: RunHistoryArt,
    icon: History,
    group: 'Run history',
    title: 'An audit trail for every change',
    body: 'Each run records who started it, the parameters, and every server’s output and exit code. Retries create new runs, so nothing is overwritten.',
    chips: ['Audit trail', 'Per-server logs', 'Filter by server'],
    to: '/features/run-history',
  },
  {
    art: MonitorArt,
    icon: Radar,
    group: 'Monitoring',
    title: 'Monitoring from outside your network',
    body: 'Websites, APIs, ports, SSL certificates, DNS records, domain expiry and server health, with incident history and alerts by email or webhook.',
    chips: ['External checks', 'Incident history', 'Email and webhook'],
    to: '/monitoring/website-monitoring',
  },
  {
    art: AutoFixArt,
    icon: Wrench,
    group: 'Auto-Fix',
    title: 'Automatic fixes for common failures',
    body: 'Restarts a stopped service, reloads a stuck PHP-FPM pool or frees disk space before alerting you. It never reboots, deletes your data or changes firewall rules.',
    chips: ['Four safe fixes', '3 attempts a day', 'Alerts if unresolved'],
    to: '/features/auto-fix',
  },
  {
    art: BackupArt,
    icon: Archive,
    group: 'Backups',
    title: 'Backups to storage you own',
    body: 'Scheduled backups of directories and MariaDB, MySQL or PostgreSQL databases to any S3-compatible bucket. Retention never deletes the last good copy.',
    chips: ['S3-compatible', 'Daily or weekly', 'Restore to another server'],
    to: '/features/backups',
  },
  {
    art: RestoreTestArt,
    icon: FlaskConical,
    group: 'Verification',
    title: 'Weekly restore tests',
    body: 'Every backup is checked at its destination, and each source’s newest backup is restored into a scratch database and read back every week.',
    chips: ['Weekly restore test', 'SHA-256 checked', 'Live data untouched'],
    to: '/features/backup-verification',
  },
];

const MONITORS = [
  {
    icon: Globe,
    title: 'Website or API',
    body: 'Status code, response time and a phrase that must appear in the response.',
  },
  {
    icon: Plug,
    title: 'Port',
    body: 'TCP connection checks for databases, mail servers and other services.',
  },
  {
    icon: ShieldCheck,
    title: 'SSL certificate',
    body: 'Expiry alerts for any host and port, well before browsers show a warning.',
  },
  {
    icon: Network,
    title: 'DNS record',
    body: 'Alerts when a record disappears or no longer contains the expected value.',
  },
  {
    icon: CalendarClock,
    title: 'Domain expiry',
    body: 'Registration expiry dates read directly from the registry.',
  },
  {
    icon: Gauge,
    title: 'Server health',
    body: 'Disk, memory and load on connected servers, from metrics collected every five minutes.',
  },
];

const CREDENTIALS = [
  {
    icon: KeyRound,
    title: 'SSH credentials',
    body: 'Passwords and keys are encrypted before they are stored and are never shown again. Each server’s host key is pinned on the first connection, and a changed key stops the connection.',
  },
  {
    icon: Lock,
    title: 'Sudo passwords',
    body: 'Stored encrypted and passed to sudo on standard input, never on the command line where other users on the server could read them.',
  },
  {
    icon: Archive,
    title: 'Backup storage keys',
    body: 'Stored encrypted and used only to sign short-lived links. Keys are never sent to your servers, and archives upload straight from your server to your bucket.',
  },
  {
    icon: Server,
    title: 'No agent',
    body: 'Nothing is installed on your servers. Everything runs over the SSH connection you configure, and if you cancel, your servers stay exactly as they are.',
  },
];

const LIMITS = [
  'It does not sell or host servers. You bring your own servers from any provider.',
  'It does not deploy your application code or manage domains and DNS. Certificates and firewall rules change only when you run the playbook that sets them up.',
  'It is not a web terminal. Commands run to completion and are recorded; nothing is interactive.',
  'It never reboots a server, deletes your data, changes firewall rules or rolls back a deployment on its own.',
  'It does not support Windows. Commands run on any Linux server, and each playbook lists the distributions it supports.',
  'Team access is on Pro and Business, with one permission level. Read-only and per-server roles are not available yet.',
];

/**
 * One heading treatment for every section.
 *
 * An eyebrow, a heading and one line of standfirst, always the same sizes and always the same gaps,
 * is most of what separates a designed page from an assembled one.
 */
function SectionHeading({
  eyebrow,
  title,
  children,
}: {
  eyebrow: string;
  title: string;
  children?: React.ReactNode;
}) {
  return (
    <div className="mx-auto max-w-2xl text-center">
      <p className="text-primary/80 text-xs font-semibold tracking-[0.16em] uppercase">{eyebrow}</p>
      <h2 className="mt-3 text-2xl font-semibold tracking-tight text-balance sm:text-3xl">{title}</h2>
      {children !== undefined && (
        <p className="text-muted-foreground mt-3 text-[15px] leading-relaxed text-balance">{children}</p>
      )}
    </div>
  );
}

/** The one button shape on the page, so a primary action is recognisable anywhere on it. */
const PRIMARY_CTA =
  'bg-primary text-primary-foreground inline-flex h-11 items-center justify-center gap-2 rounded-lg px-6 text-[15px] font-medium transition-opacity hover:opacity-90';
const SECONDARY_CTA =
  'border-border/60 hover:bg-muted/50 inline-flex h-11 items-center justify-center gap-2 rounded-lg border px-6 text-[15px] font-medium transition-colors';

function Chip({ children }: { children: React.ReactNode }) {
  return (
    <span className="border-border/60 text-muted-foreground rounded-full border px-2.5 py-1 text-xs">
      {children}
    </span>
  );
}

export function LandingPage() {
  usePageMeta(PAGES.home);

  return (
    <div className="bg-background min-h-screen">
      <SiteHeader onLanding />

      {/* ── Hero ───────────────────────────────────────────────────────────────────────────
          The claim on one side and the evidence for it on the other, read together. The evidence
          is the stages one change goes through on its way to a fleet. See HeroPanel. */}
      <section className="relative overflow-hidden">
        <div
          aria-hidden
          className="from-primary/10 pointer-events-none absolute inset-x-0 top-0 h-[28rem] bg-gradient-to-b to-transparent"
        />
        <div className="relative mx-auto max-w-6xl px-6 pt-14 pb-16 sm:pt-20 sm:pb-24">
          {/* grid-cols-1, not an implicit column: an implicit one is as wide as its widest line,
              and on a phone HeroPanel's lines made it 372px on a 327px screen. The section's
              overflow-hidden then cut the right edge off the buttons, the text and the panel. */}
          <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-[1.05fr_1fr] lg:gap-16">
            <div>
              {/* The category, the promise, then the mechanism. The headline says what the product
                  is for in seven words; the paragraph under it says how it saves time (one run
                  across the fleet) and why it is safer than doing it by hand (checked everywhere,
                  one server first, everything recorded). */}
              <p className="text-primary/80 text-xs font-semibold tracking-[0.16em] uppercase">
                Infrastructure operations platform
              </p>
              <h1 className="mt-4 text-4xl font-semibold tracking-[-0.025em] text-balance sm:text-5xl lg:text-[3.25rem] lg:leading-[1.05]">
                Automate your Linux servers.
                <br />
                <span className="text-muted-foreground">Keep them running.</span>
              </h1>

              <p className="text-muted-foreground mt-6 max-w-xl text-lg leading-relaxed">
                Run tested playbooks and commands across all your servers in one run. Every change is
                checked on each server and tried on one before the rest. Monitoring, Auto-Fix and
                restore-tested backups are built in.
              </p>

              {/* Full width and stacked on a phone: the two labels do not fit one row there, and
                  side by side they wrapped into two ragged rows of different widths. */}
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/register" className={`${PRIMARY_CTA} w-full sm:w-auto`}>
                  Start free trial
                  <ArrowRight className="size-4" aria-hidden />
                </Link>
                {/* The no-commitment path: the demo needs no account and ends at the same sign-up.
                    Pricing is in the header on every page, so this slot is not needed for it. */}
                <a href="#demo" className={`${SECONDARY_CTA} w-full sm:w-auto`}>
                  <Play className="size-4" aria-hidden />
                  Try the live demo
                </a>
              </div>

              {/* Who it is for, and facts about the product. Nothing about customer numbers, logos
                  or ratings, because there are none to state. */}
              <p className="text-muted-foreground mt-6 text-sm leading-relaxed">
                Built for sysadmins, MSPs, hosting providers and agencies.
                <br className="hidden sm:block" /> {TRIAL.days}-day free trial, no card · No agent to install · Root
                or sudo
              </p>
            </div>

            <HeroPanel />
          </div>
        </div>
      </section>

      {/* ── Providers ──────────────────────────────────────────────────────────────────── */}
      <section className="border-border/60 border-y">
        <div className="mx-auto max-w-6xl px-6 py-12">
          <p className="text-muted-foreground/70 text-center text-xs font-semibold tracking-[0.18em] uppercase">
            Works with servers from any provider
          </p>
          <div className="mx-auto mt-7 flex max-w-4xl flex-wrap items-center justify-center gap-x-9 gap-y-4">
            {PROVIDERS.map((provider) => (
              <span
                key={provider}
                className="text-muted-foreground/50 hover:text-foreground/80 text-base font-semibold tracking-tight transition-colors duration-200 sm:text-lg"
              >
                {provider}
              </span>
            ))}
          </div>
          <p className="text-muted-foreground/60 mx-auto mt-7 max-w-xl text-center text-xs leading-relaxed">
            Ubuntu, Debian, RHEL-compatible and Amazon Linux servers you can reach over SSH. Techie
            Gain does not resell servers and is not affiliated with these providers.
          </p>
        </div>
      </section>

      {/* ── How it works ───────────────────────────────────────────────────────────────── */}
      <section id="how" className={`mx-auto max-w-6xl px-6 ${SECTION}`}>
        <SectionHeading eyebrow="How it works" title="Three steps to a managed fleet">
          No agent, no inventory files and no configuration language to learn.
        </SectionHeading>

        <div className="mt-12 grid gap-5 md:grid-cols-3">
          {[
            {
              n: '01',
              title: 'Connect your servers',
              body: 'Add servers over SSH as root or a user with sudo. The platform detects the distribution, package manager, root access, CPU, memory and disk.',
            },
            {
              n: '02',
              title: 'Run automation safely',
              body: 'Choose servers by name, group, tag or operating system. Each run is checked on every server, can be dry-run first, and goes to one canary server before the rest.',
            },
            {
              n: '03',
              title: 'Keep servers healthy',
              body: 'Monitors check from outside your network and try a safe fix before alerting you. Backups go to storage you own and are restore-tested every week.',
            },
          ].map((step) => (
            <div key={step.n} className="border-border/60 bg-muted/10 rounded-xl border p-6">
              <span className="text-primary/60 font-mono text-xs font-semibold">{step.n}</span>
              <h3 className="mt-3 font-medium">{step.title}</h3>
              <p className="text-muted-foreground mt-2 text-sm leading-relaxed">{step.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Live demo ──────────────────────────────────────────────────────────────────────
          The run the hero describes, with the visitor pressing the button a real run waits for.
          Straight after "How it works", so preflight and canary are words just read. See
          RolloutDemo. */}
      <section id="demo" className="border-border/60 relative overflow-hidden border-t">
        <div
          aria-hidden
          className="from-primary/[0.07] pointer-events-none absolute inset-x-0 top-0 h-80 bg-gradient-to-b to-transparent"
        />
        <div className={`relative mx-auto max-w-6xl px-6 ${SECTION}`}>
          <RolloutDemo />
        </div>
      </section>

      {/* ── The platform ───────────────────────────────────────────────────────────────── */}
      <section id="features" className="border-border/60 border-y">
        <div className={`mx-auto max-w-6xl px-6 ${SECTION}`}>
          <SectionHeading eyebrow="Platform" title="Automation, monitoring and backups in one place">
            Automation changes your servers, monitoring with Auto-Fix keeps them available, and verified
            backups give you a way back.
          </SectionHeading>

          <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {CAPABILITIES.map((capability) => (
              <Link
                key={capability.group}
                to={capability.to}
                className="border-border/60 bg-background hover:border-primary/30 hover:bg-muted/20 group flex flex-col rounded-xl border p-5 transition-colors"
              >
                <capability.art />
                <div className="mt-5 flex items-center gap-2">
                  <capability.icon className="text-primary size-4 shrink-0" aria-hidden />
                  <p className="text-muted-foreground text-xs font-semibold tracking-[0.12em] uppercase">
                    {capability.group}
                  </p>
                </div>
                <h3 className="mt-3 leading-snug font-medium text-balance">{capability.title}</h3>
                <p className="text-muted-foreground mt-2.5 flex-1 text-sm leading-relaxed">{capability.body}</p>
                <div className="mt-4 flex flex-wrap gap-1.5">
                  {capability.chips.map((chip) => (
                    <Chip key={chip}>{chip}</Chip>
                  ))}
                </div>

                <span className="text-primary mt-4 inline-flex items-center gap-1.5 text-sm font-medium">
                  Learn more
                  <span className="sr-only"> about {capability.group}</span>
                  <ArrowRight className="size-4 transition-transform group-hover:translate-x-0.5" aria-hidden />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── What automation runs on ────────────────────────────────────────────────────────
          The question somebody with a mixed fleet arrives with is "does it cope with mine". The
          answer is a list of distributions and a list of jobs, both straight from the code. */}
      <section id="automation" className={`mx-auto max-w-6xl px-6 ${SECTION}`}>
        <SectionHeading eyebrow="Automation" title="Built for mixed Linux fleets">
          Playbooks detect each server’s distribution and package manager, so one run can update
          Ubuntu, Debian and RHEL-compatible servers together.
        </SectionHeading>

        <div className="mt-12 grid gap-5 lg:grid-cols-3">
          <div className="border-border/60 bg-muted/10 rounded-xl border p-6">
            <h3 className="font-medium">Distributions</h3>
            <p className="text-muted-foreground mt-2 text-sm leading-relaxed">
              Playbooks support Ubuntu 22.04 and later, Debian 12 and later and RHEL-compatible 8 and
              later, and most run on Amazon Linux 2023. The preflight rejects an unsupported server before
              anything changes.
            </p>
            <div className="mt-4 flex flex-wrap gap-1.5">
              {DISTRIBUTIONS.map((name) => (
                <Chip key={name}>{name}</Chip>
              ))}
            </div>
          </div>

          <div className="border-border/60 bg-muted/10 rounded-xl border p-6">
            <h3 className="font-medium">Playbooks</h3>
            <p className="text-muted-foreground mt-2 text-sm leading-relaxed">
              Twenty-four maintenance, security, user and install tasks, each with a dry run, automatic
              retries for temporary failures, a time limit and output that shows what changed.
            </p>
            <div className="mt-4 flex flex-wrap gap-1.5">
              {PLAYBOOKS.map((name) => (
                <Chip key={name}>{name}</Chip>
              ))}
            </div>
          </div>

          <div className="border-border/60 bg-muted/10 rounded-xl border p-6">
            <h3 className="font-medium">Templates</h3>
            <p className="text-muted-foreground mt-2 text-sm leading-relaxed">
              Versioned sequences of playbooks that prepare a server for one role. Your application
              and its data stay yours to deploy.
            </p>
            <div className="mt-4 flex flex-wrap gap-1.5">
              {TEMPLATES.map((name) => (
                <Chip key={name}>{name}</Chip>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Monitoring ─────────────────────────────────────────────────────────────────────
          Same container, same heading treatment and same card shape as the sections around it,
          because it is a part of the product rather than a campaign about one. */}
      <section id="monitoring" className="border-border/60 border-y">
        <div className={`mx-auto max-w-6xl px-6 ${SECTION}`}>
          <SectionHeading eyebrow="Monitoring and Auto-Fix" title="Monitoring that fixes common failures">
            Checks run from outside your network, so a server that goes down completely is still
            detected. Monitors attached to a server can try a safe fix before alerting you.
          </SectionHeading>

          <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {MONITORS.map((monitor) => (
              <article
                key={monitor.title}
                className="border-border/60 bg-background hover:bg-muted/20 rounded-xl border p-5 transition-colors"
              >
                <div className="flex items-center gap-2.5">
                  <span className="bg-primary/10 text-primary inline-flex size-8 shrink-0 items-center justify-center rounded-lg">
                    <monitor.icon className="size-4" aria-hidden />
                  </span>
                  <h3 className="font-medium">{monitor.title}</h3>
                </div>
                <p className="text-muted-foreground mt-3 text-sm leading-relaxed">{monitor.body}</p>
              </article>
            ))}
          </div>

          <div className="mx-auto mt-10 grid max-w-4xl gap-4 md:grid-cols-2">
            <div className="border-border/60 bg-muted/10 flex items-start gap-3 rounded-xl border p-5">
              <Radar className="text-primary mt-0.5 size-5 shrink-0" aria-hidden />
              <p className="text-muted-foreground text-sm leading-relaxed">
                A failure must repeat before it counts, so a single blip is not an outage. Each outage
                becomes an incident with its start time and what the check saw, and alerts go out by
                email or webhook.
              </p>
            </div>
            <div className="border-border/60 bg-muted/10 flex items-start gap-3 rounded-xl border p-5">
              <Wrench className="text-primary mt-0.5 size-5 shrink-0" aria-hidden />
              <p className="text-muted-foreground text-sm leading-relaxed">
                Auto-Fix restarts stopped services, reloads PHP-FPM or the web server, or frees disk
                space, at most three times a day per monitor. The alert waits about a minute and is sent
                if the fix does not work.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Credentials ────────────────────────────────────────────────────────────────── */}
      <section className={`mx-auto max-w-6xl px-6 ${SECTION}`}>
        <SectionHeading eyebrow="Security" title="How your credentials are handled">
          What the platform stores, how it is protected and how each secret is used.
        </SectionHeading>

        <div className="mt-12 grid gap-4 sm:grid-cols-2">
          {CREDENTIALS.map((item) => (
            <div key={item.title} className="border-border/60 bg-background rounded-xl border p-6">
              <item.icon className="text-primary size-5" aria-hidden />
              <h3 className="mt-4 font-medium">{item.title}</h3>
              <p className="text-muted-foreground mt-2 text-sm leading-relaxed">{item.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Scope, placed where a decision is being made ───────────────────────────────── */}
      <section className="border-border/60 border-y">
        <div className={`mx-auto max-w-6xl px-6 ${SECTION}`}>
          <SectionHeading eyebrow="Scope" title="What Techie Gain does not do">
            Worth knowing before you sign up.
          </SectionHeading>

          <div className="mx-auto mt-10 grid max-w-4xl gap-3 sm:grid-cols-2">
            {LIMITS.map((line) => (
              <p
                key={line}
                className="text-muted-foreground border-border/60 bg-background rounded-lg border p-4 text-sm leading-relaxed"
              >
                {line}
              </p>
            ))}
          </div>
        </div>
      </section>

      {/* ── Pricing ────────────────────────────────────────────────────────────────────── */}
      <section id="pricing" className={`mx-auto max-w-6xl px-6 ${SECTION}`}>
        <SectionHeading eyebrow="Pricing" title="Automation, monitoring and backups on every plan">
          Plans differ by how many servers, monitors and team members they include. Pay yearly and get
          two months free.
        </SectionHeading>

        <PricingTable />
      </section>

      {/* ── FAQ ────────────────────────────────────────────────────────────────────────── */}
      <section id="faq" className="border-border/60 border-t">
        <div className={`mx-auto max-w-5xl px-6 ${SECTION}`}>
          <SectionHeading eyebrow="FAQ" title="Frequently asked questions" />

          <div className="mt-12 grid gap-4 sm:grid-cols-2">
            {LANDING_FAQ.map((item) => (
              <div key={item.q} className="border-border/60 bg-muted/10 rounded-xl border p-6">
                <h3 className="font-medium">{item.q}</h3>
                <p className="text-muted-foreground mt-2 text-sm leading-relaxed">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Final CTA ──────────────────────────────────────────────────────────────────── */}
      <section className="mx-auto max-w-6xl px-6 pb-20">
        <div className="border-border/60 from-primary/10 rounded-2xl border bg-gradient-to-b to-transparent px-6 py-14 text-center">
          <h2 className="text-2xl font-semibold tracking-tight text-balance sm:text-3xl">
            Ready to connect your servers?
          </h2>
          <p className="text-muted-foreground mx-auto mt-3 max-w-xl leading-relaxed">
            Start with a {TRIAL.days}-day free trial on {countOf(TRIAL.servers, 'server', 'servers')}, with no
            card. There is no agent to install, and your servers keep running whatever you decide.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/register" className={PRIMARY_CTA}>
              Start free trial
              <ArrowRight className="size-4" aria-hidden />
            </Link>
            <Link to="/docs" className={SECONDARY_CTA}>
              Read the documentation
            </Link>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
